import { useEffect, useState } from 'react';

// Light / dark toggle. Sets `data-theme` on <html> and persists to
// localStorage. The blocking init script in index.html applies the saved
// theme before first paint, so there's no flash on reload — this just
// keeps React in sync and flips it on click.

type Theme = 'light' | 'dark';
const STORAGE_KEY = 'moxo-funnel-theme';

export default function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof document === 'undefined') return 'light';
    return (document.documentElement.getAttribute('data-theme') as Theme | null) ?? 'light';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* private mode / disabled storage — accept ephemeral preference */
    }
  }, [theme]);

  const next: Theme = theme === 'dark' ? 'light' : 'dark';

  return (
    <button
      type="button"
      className="theme-toggle"
      onClick={() => setTheme(next)}
      aria-label={`Switch to ${next} mode`}
      title={`Switch to ${next} mode`}
    >
      {theme === 'dark' ? <MoonIcon /> : <SunIcon />}
      <span>{theme}</span>
    </button>
  );
}

function SunIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor">
      <path d="M11 2.5a5.5 5.5 0 1 0 2.5 10.4 6.5 6.5 0 0 1-9-9A5.5 5.5 0 0 1 11 2.5z" />
    </svg>
  );
}
